import {
  AgreementType,
  ApplicationProductTypeEnum,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  router,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Share, StyleSheet, View } from 'react-native';
import PdfViewer from 'shared/components/PdfRenderer/PdfViewer';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import useAgreements from 'shared/hooks/useAgreements/useAgreements';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';

const agreementTypeMap: Record<string, AgreementType> = {
  [ApplicationProductTypeEnum.Advance]: AgreementType.AdvanceAgreement,
  [ApplicationProductTypeEnum.AdvanceV2]: AgreementType.AdvanceV2Agreement,
  [ApplicationProductTypeEnum.AdvancePlaidLinking]:
    AgreementType.AdvanceV2PlaidLinkingAgreement,
  [ApplicationProductTypeEnum.LineOfCredit]:
    AgreementType.LineOfCreditAgreement,
  [ApplicationProductTypeEnum.PayOverTime]: AgreementType.PayOverTimeAgreement,
};

const PdfScreen = () => {
  const { agreementType, title } = useLocalSearchParams<{
    agreementType: ApplicationProductTypeEnum;
    title?: string;
  }>();

  const { t: translatedText } = useTranslation('lending', {
    keyPrefix: 'pdfScreen',
  });

  const {
    agreementsListDetails: agreementsData,
    loading: isLoading,
    errorModel,
  } = useAgreements([agreementType]);

  const type = agreementTypeMap[agreementType];

  const agreementPdfUrl =
    agreementsData?.find(
      (agreement: { type: AgreementType }) => agreement.type === type,
    )?.resource?.pdfDocument?.url ?? '';
  const onShare = async () => {
    await Share.share({
      message: agreementPdfUrl,
    });
  };

  const isAdvance = [
    ApplicationProductTypeEnum.Advance,
    ApplicationProductTypeEnum.AdvanceV2,
    ApplicationProductTypeEnum.AdvancePlaidLinking,
  ].includes(agreementType);

  const [shouldNavigate, setShouldNavigate] = useState(false);

  useEffect(() => {
    if (
      agreementsData?.length === 0 &&
      agreementPdfUrl === '' &&
      !isLoading &&
      isAdvance
    ) {
      setShouldNavigate(true);
    }
  }, [agreementPdfUrl, agreementsData, isAdvance, isLoading]);

  useEffect(() => {
    if (shouldNavigate) {
      router.navigate({
        pathname: '/varo-advance/error',
        params: {
          subcategory: 'va_application',
          error_type: ErrorScreenType.Error,
          error_description: 'PDF not found',
          step_name: 'employment_info',
        },
      });
    }
  }, [shouldNavigate]);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: title ?? translatedText('title'),
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              trailingAccessories={
                <IconButton
                  iconKey="SHARE"
                  iconButtonAccessibilityLabel={translatedText('share')}
                  iconOnPress={onShare}
                />
              }
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={isLoading && !errorModel}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorModel ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={errorModel.messageHeadline}
              messageBody={errorModel.messageBody}
              primaryButton={{
                title: translatedText('errorButton'),
                onPress: () => router.replace('/'),
              }}
            />
          ) : null
        }
      />
      {agreementPdfUrl ? <PdfViewer url={agreementPdfUrl} /> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default PdfScreen;
